import StorageService from '@/services/storage.service'
import storageKeys from '@/constants/storageKeys'
import { defaultFilters } from '@/models/Postbacks/helpers'
import postbacksRepository from '@/repository/generatedRepository/postbacksRepository'
import TableOptions from '@/models/TableOptions'
import { moveFiltersToCustom, omitEmptyDeepFilters, prepareFilters } from '@/helpers/filters'
import { canFitInLocalStorage } from '@/helpers/reports'
import { noDataPagination } from '@/helpers/render-value'

// sent as custom_filter[...] (see moveFiltersToCustom)
const CUSTOM_FILTER_KEYS = ['retargeting']

const state = {
  list: [],
  filters: StorageService.get(storageKeys.POSTBACKS_FILTERS) || { ...defaultFilters() },
  tableOptions: TableOptions.defaultTableOptions(),
}

const getters = {
  filters: state => state.filters,
  tableOptions: state => state.tableOptions,
  list: state => state.list,
  isOpenFilterPanel: state =>
    state.filters.clientOptions && state.filters.clientOptions.filterPanelExpanded,
}

const actions = {
  async loadData(context, payload) {
    const {signal, updateData = true, ...filters} = payload
    context.commit('setFilters', filters)
    StorageService.set(storageKeys.POSTBACKS_FILTERS, filters)

    const requestFilters = moveFiltersToCustom(filters, CUSTOM_FILTER_KEYS)
    const appliedFilters = omitEmptyDeepFilters(requestFilters.filter)
    const appliedCfFilters = omitEmptyDeepFilters(filters.cfFilter)

    const preparedFilters = prepareFilters({
      ...requestFilters,
      filter: appliedFilters,
      cfFilter: appliedCfFilters,
      signal
    })

    let data = {}
    if (!updateData) {
      data = StorageService.get(storageKeys.POSTBACKS_DATA) || noDataPagination
    } else {
      data = (await postbacksRepository.list(preparedFilters)).data
    }

    if (canFitInLocalStorage(data)) {
      StorageService.set(storageKeys.POSTBACKS_DATA, data)
    } else {
      StorageService.remove(storageKeys.POSTBACKS_DATA)
    }

    context.commit('setList', { data, filters })

    return { products: data.items, pagination: data.pagination }
  },
  async loadPartnerData(context, filters) {
    const requestFilters = moveFiltersToCustom(filters, CUSTOM_FILTER_KEYS)
    const appliedFilters = omitEmptyDeepFilters(requestFilters.filter)
    const preparedFilters = prepareFilters({
      ...requestFilters,
      filter: appliedFilters,
    })
    const { data } = await postbacksRepository.list(preparedFilters)
    context.commit('setList', { data, filters })
    return { products: data.items, pagination: data.pagination }
  },
  async loadExportFile(context, filters) {
    const requestFilters = moveFiltersToCustom(filters, CUSTOM_FILTER_KEYS)
    const appliedFilters = omitEmptyDeepFilters(requestFilters.filter)
    const preparedFilters = prepareFilters({
      ...requestFilters,
      filter: appliedFilters,
    })
    const { data } = await postbacksRepository.exportPostbacksReportToken({...preparedFilters})
    window.location.href = process.env.VUE_APP_REPORTS_API_ORIGIN + data.url
  },
  async searchPostbacks(context, filters) {
    const requestFilters = moveFiltersToCustom(filters, CUSTOM_FILTER_KEYS)
    const appliedFilters = omitEmptyDeepFilters(requestFilters.filter)
    const preparedFilters = prepareFilters({
      ...requestFilters,
      filter: appliedFilters,
    })

    const { data } = await postbacksRepository.list(preparedFilters)

    return { products: data.items, pagination: data.pagination }
  },
  async loadItem(context, id) {
    const { data } = await postbacksRepository.get(id)
    return data
  },
  createItem(context, payload) {
    return postbacksRepository.post(payload)
  },
  resendPostbacks(context, payload) {
    return postbacksRepository.resendPostbacks(payload)
  },
  updateItem(context, payload) {
    const { id, ...data } = payload
    return postbacksRepository.put(id, data)
  },
  deleteItem(context, payload) {
    return postbacksRepository.delete(payload)
  },
  filtersStorageReset() {
    StorageService.remove(storageKeys.POSTBACKS_FILTERS)
  },
  resetSearchFilter({commit}){
    commit('clearSearch')
  },
  updateFiltersClientOptions({ commit, getters }, clientOptions) {
    const updatedFilters = {
      ...getters.filters,
      clientOptions,
    }
    commit('setFilters', updatedFilters)
    StorageService.set(storageKeys.POSTBACKS_FILTERS, updatedFilters)
  },
}

const mutations = {
  setList(state, { data, filters }) {
    state.list = [...data.items]
    state.tableOptions = new TableOptions(data.pagination, filters)
  },
  setFilters(state, filters) {
    state.filters = { ...filters }
  },
  clearSearch(state) {
    state.tableOptions.search = ''
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
